import React from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import Button from './ui/Button';
import { FaCrown, FaExclamationTriangle } from 'react-icons/fa';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  linkCount: number;
  linkLimit: number;
}

const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  linkCount,
  linkLimit
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          position: 'relative'
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: theme.colors.text.secondary,
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.colors.background;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          ×
        </button>

        {/* Icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#FEF3C7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#D97706'
          }}>
            {React.createElement(FaExclamationTriangle as React.ComponentType<any>, { size: 40 })}
          </div>
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 16px 0',
          fontSize: theme.typography.sizes['2xl'],
          fontWeight: theme.typography.weights.bold,
          color: theme.colors.text.primary,
          textAlign: 'center'
        }}>
          Link Limit Reached
        </h2>

        {/* Message */}
        <p style={{
          margin: '0 0 24px 0',
          fontSize: theme.typography.sizes.base,
          color: theme.colors.text.secondary,
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          You've reached your limit of <strong>{linkLimit} trackable links</strong> on the free plan.
          You currently have <strong>{linkCount} links</strong>.
        </p>

        {/* Benefits */}
        <div style={{
          backgroundColor: theme.colors.background,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          border: `1px solid ${theme.colors.border}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <div style={{ color: '#D97706' }}>
              {React.createElement(FaCrown as React.ComponentType<any>, { size: 20 })}
            </div>
            <h3 style={{
              margin: 0,
              fontSize: theme.typography.sizes.lg,
              fontWeight: theme.typography.weights.semibold,
              color: theme.colors.text.primary
            }}>
              Upgrade to Pro
            </h3>
          </div>
          <ul style={{
            margin: '0',
            padding: '0 0 0 32px',
            fontSize: theme.typography.sizes.sm,
            color: theme.colors.text.secondary,
            lineHeight: '2'
          }}>
            <li>Unlimited trackable links</li>
            <li>Advanced analytics</li>
            <li>Priority support</li>
            <li>Custom branding</li>
            <li>API access</li>
          </ul>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'stretch'
        }}>
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleUpgrade}
            style={{
              flex: 1,
              backgroundColor: '#D97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {React.createElement(FaCrown as React.ComponentType<any>, { size: 16 })}
            <span>Upgrade to Pro</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePromptModal;
